import { useState, useEffect } from 'react';
import { CopyIcon, QrCodeIcon, UserIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon } from './icons';
import QRCodeModal from './QRCodeModal';
import { useClipStore } from '../stores/clipStore';
import { useI18n } from '../i18n';

interface ShareBarProps {
  url: string;
  pin: string;
}

export default function ShareBar({ url, pin }: ShareBarProps) {
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState<'url' | 'pin' | null>(null);
  const peerCount = useClipStore((state) => state.peerCount);
  const isCreator = useClipStore((state) => state.isCreator);
  const createdAt = useClipStore((state) => state.createdAt);
  const { t } = useI18n();

  // Determine if PIN should be shown by default (within 5 minutes of creation)
  const isNewlyCreated = createdAt && (Date.now() - createdAt < 5 * 60 * 1000);
  const [showPin, setShowPin] = useState(isNewlyCreated);

  // Auto-hide PIN after 5 minutes from creation
  useEffect(() => {
    if (!createdAt || !isCreator) return;

    const elapsed = Date.now() - createdAt;
    const fiveMinutes = 5 * 60 * 1000;

    if (elapsed < fiveMinutes) {
      const timeoutId = setTimeout(() => {
        setShowPin(false);
      }, fiveMinutes - elapsed);
      return () => clearTimeout(timeoutId);
    }
  }, [createdAt, isCreator]);

  const handleCopy = async (type: 'url' | 'pin', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (e) {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  return (
    <>
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          {isCreator ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* URL Share */}
              <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-500 truncate flex-1">{url}</span>
                <button
                  onClick={() => handleCopy('url', url)}
                  className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
                  title={t.shareBar.copyUrl}
                >
                  {copied === 'url' ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-600" />
                  ) : (
                    <CopyIcon className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                <button
                  onClick={() => setShowQR(true)}
                  className="p-1.5 hover:bg-gray-200 rounded-md transition-colors"
                  title={t.shareBar.showQR}
                >
                  <QrCodeIcon className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* PIN */}
              <div className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                {showPin ? (
                  <>
                    <span className="text-sm text-gray-600">{t.shareBar.pin}</span>
                    <span className="text-lg font-mono font-bold text-blue-700 tracking-wider">{pin}</span>
                    <button
                      onClick={() => handleCopy('pin', pin)}
                      className="p-1.5 hover:bg-blue-100 rounded-md transition-colors"
                      title={t.shareBar.copyPin}
                    >
                      {copied === 'pin' ? (
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      ) : (
                        <CopyIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => setShowPin(false)}
                      className="p-1.5 hover:bg-blue-100 rounded-md transition-colors"
                      title={t.shareBar.hidePin}
                    >
                      <EyeSlashIcon className="w-4 h-4 text-blue-600" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-600">{t.shareBar.pin}</span>
                    <span className="text-sm text-gray-500">••••••</span>
                    <button
                      onClick={() => setShowPin(true)}
                      className="p-1.5 hover:bg-blue-100 rounded-md transition-colors"
                      title={t.shareBar.showPin}
                    >
                      <EyeIcon className="w-4 h-4 text-blue-600" />
                    </button>
                  </>
                )}
              </div>

              {/* Peer Count */}
              <div className="flex items-center gap-1.5 text-sm text-gray-600 px-2">
                <UserIcon className="w-4 h-4" />
                <span>{peerCount} {t.shareBar.connections}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">{t.shareBar.connected}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {t.clip.encrypted}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <UserIcon className="w-4 h-4" />
                <span>{peerCount} {t.shareBar.connections}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <QRCodeModal url={url} isOpen={showQR} onClose={() => setShowQR(false)} />
    </>
  );
}
